import React from 'react';
import { motion } from 'motion/react';
import { 
  Sliders, Compass, CheckCircle, ArrowUpRight, Sparkles,
  Zap, Layers, TrendingUp, Users, Mic, Package, Printer, Video, 
  Database, Server, Globe, Mail, Key, FileText, Map, Activity, 
  Shield, UserCheck, CreditCard, Target, Cpu, Crosshair, Rocket
} from 'lucide-react';
import { CATEGORIES } from '../data';

interface CosmicLandingHomeTabProps {
  onNavigateLandingTab?: (tab: string) => void;
  onSelectCategory?: (categoryTitle: string, subNode?: string) => void;
}

export const CosmicLandingHomeTab: React.FC<CosmicLandingHomeTabProps> = ({
  onNavigateLandingTab,
  onSelectCategory
}) => {
  const tickerItems = [
    'Web Design',
    'AI Automation',
    'Nexus Search',
    'Nexus Hosting',
    'Mail & Domains',
    'WorkSuite',
    'Media Studio',
    'Social Automation',
    'Business Growth',
    'Client Dashboard',
    'Public Preview Ready'
  ];

  // Tripled stream list to ensure infinite smooth marquee without gaps
  const roundedTickerItems = [...tickerItems, ...tickerItems, ...tickerItems];

  return (
    <div className="w-full min-h-[90vh] flex flex-col items-center relative z-10 pb-16 px-0 pt-16 md:pt-24 select-none">
       
       {/* Embed state-of-the-art cinematic luxury effects and styles */}
       <style>{`
          /* Abstract Liquid Glass design styles */
          @keyframes fluid-flow-x {
            0% { transform: translate(-30%, -30%) rotate(0deg) scale(1); }
            50% { transform: translate(15%, 15%) rotate(180deg) scale(1.15); }
            100% { transform: translate(-30%, -30%) rotate(360deg) scale(1); }
          }
          @keyframes fluid-flow-y {
            0% { transform: translate(25%, -20%) rotate(360deg) scale(1.1); }
            50% { transform: translate(-10%, 20%) rotate(180deg) scale(0.9); }
            100% { transform: translate(25%, -20%) rotate(0deg) scale(1.1); }
          }
          @keyframes fluid-flow-z {
            0% { transform: translate(-10%, 15%) rotate(0deg) scale(0.95); }
            50% { transform: translate(20%, -30%) rotate(-180deg) scale(1.1); }
            100% { transform: translate(-10%, 15%) rotate(-360deg) scale(0.95); }
          }
          .liquid-glass-blob-1 {
            animation: fluid-flow-x 24s ease-in-out infinite;
          }
          .liquid-glass-blob-2 {
            animation: fluid-flow-y 20s ease-in-out infinite;
          }
          .liquid-glass-blob-3 {
            animation: fluid-flow-z 28s ease-in-out infinite;
          }
          
          @keyframes liquid-text-shimmer {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
          
          /* Liquid glass textured typography and refraction sheen */
          .liquid-glass-heading-text {
            background: linear-gradient(
              120deg,
              #ffffff 0%,
              #e0f2fe 15%,
              #22d3ee 33%,
              #3b82f6 48%,
              #c084fc 64%,
              #f43f5e 80%,
              #ffffff 100%
            );
            background-size: 200% auto;
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            animation: liquid-text-shimmer 8s ease-in-out infinite;
            filter: drop-shadow(0 2px 10px rgba(34, 211, 238, 0.45));
          }

          .liquid-glass-tagline {
            background: linear-gradient(135deg, rgba(255, 255, 255, 0.06) 0%, rgba(255, 255, 255, 0.01) 100%);
            border: 1px solid rgba(255, 255, 255, 0.15);
            box-shadow: 
              inset 0 1px 1.5px rgba(255, 255, 255, 0.3),
              0 8px 32px rgba(0, 0, 0, 0.35),
              0 0 15px rgba(34, 211, 238, 0.12);
            backdrop-filter: blur(20px);
          }

          .liquid-glass-panel {
            background: linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.01) 100%);
            border: 1.5px solid rgba(255, 255, 255, 0.32);
            box-shadow: 
              inset 0 1.5px 3.5px rgba(255, 255, 255, 0.5),
              inset 0 -1.5px 3px rgba(0, 0, 0, 0.35),
              inset 0 0 0 1px rgba(253, 224, 71, 0.08),
              0 25px 60px rgba(0, 0, 0, 0.55),
              0 0 20px rgba(0, 240, 255, 0.06);
            /* Crisp low blur so the background video plays beautifully and remains highly visible through the panel */
            backdrop-filter: blur(8px) saturate(180%);
          }

          .liquid-glass-btn-primary {
            background: linear-gradient(135deg, rgba(0, 240, 255, 0.22) 0%, rgba(15, 23, 42, 0.45) 50%, rgba(0, 240, 255, 0.15) 100%);
            border: 1px solid rgba(0, 240, 255, 0.4);
            box-shadow: 
              inset 0 1px 2px rgba(255, 255, 255, 0.25),
              0 0 20px rgba(0, 240, 255, 0.25);
            backdrop-filter: blur(20px);
            transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
          }
          .liquid-glass-btn-primary:hover {
            background: linear-gradient(135deg, rgba(0, 240, 255, 0.45) 0%, rgba(59, 130, 246, 0.45) 100%);
            border: 1px solid rgba(255, 255, 255, 0.55);
            box-shadow: 
              inset 0 1px 3px rgba(255, 255, 255, 0.45),
              0 0 32px rgba(0, 240, 255, 0.5);
            transform: translateY(-1px) scale(1.025);
          }

          .liquid-glass-btn-secondary {
            background: linear-gradient(135deg, rgba(255, 255, 255, 0.04) 0%, rgba(255, 255, 255, 0.01) 100%);
            border: 1px solid rgba(255, 255, 255, 0.14);
            box-shadow: 
              inset 0 1px 1.5px rgba(255, 255, 255, 0.18),
              0 10px 30px rgba(0, 0, 0, 0.45);
            backdrop-filter: blur(20px);
            transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
          }
          .liquid-glass-btn-secondary:hover {
            background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.03) 100%);
            border: 1px solid rgba(255, 255, 255, 0.32);
            box-shadow: 
              inset 0 1px 2.5px rgba(255, 255, 255, 0.3),
              0 0 25px rgba(255, 255, 255, 0.18);
            transform: translateY(-1px) scale(1.025);
          }

          @keyframes gentle-float {
            0% { transform: translateY(0px) rotate(0deg); }
            50% { transform: translateY(-8px) rotate(0.4deg); }
            100% { transform: translateY(0px) rotate(0deg); }
          }
          .floating-hero-panel {
            animation: gentle-float 9s ease-in-out infinite;
          }
          @keyframes soft-pulse-glow {
            0%, 100% { opacity: 0.18; transform: translate(-50%, -50%) scale(1); }
            50% { opacity: 0.28; transform: translate(-50%, -50%) scale(1.08); }
          }
          .glow-orb-premium {
            animation: soft-pulse-glow 11s ease-in-out infinite;
          }
          @keyframes custom-marquee-forward {
            0% { transform: translateX(0%); }
            100% { transform: translateX(-50%); }
          }
          .ticker-animate-forward {
            display: flex;
            width: max-content;
            animation: custom-marquee-forward 35s linear infinite;
          }
          @media (prefers-reduced-motion: reduce) {
            .ticker-animate-forward {
              animation-play-state: paused !important;
            }
          }
          @keyframes custom-marquee {
            0% { transform: translateX(0%); }
            100% { transform: translateX(-50%); }
          }
        `}</style>

        {/* Dynamic Header Badge / Quick Status ticker */}
        <div className="w-full max-w-7xl mx-auto px-6 mb-8 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 text-[8px] font-mono uppercase text-slate-500 pointer-events-auto">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1.5"><Sliders className="w-3.5 h-3.5 text-cyan-400" /> SYS_LEVEL: SINGULARITY_OS</span>
            <span className="text-[#00f0ff] font-bold">||</span>
            <span className="flex items-center gap-1"><Compass className="w-3 h-3 text-cyan-400" /> GEO: SC887095</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1 border border-cyan-500/15 py-0.5 px-2 rounded bg-cyan-400/5 text-cyan-300 font-black"><CheckCircle className="w-3 h-3 text-cyan-400" /> LEDGER SYNC Nominal</span>
            <span>TIME_UTC: {new Date().toISOString().substring(11, 19)}</span>
          </div>
        </div>

        {/* ACTIVE SINGLE HERO SECTION (PREMIUM LIQUID GLASSMORPHISM) */}
        <div className="w-full max-w-7xl mx-auto px-6 z-20 pointer-events-auto flex flex-col gap-8">
          <motion.div 
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.0, ease: "easeOut" }}
            className="liquid-glass-panel rounded-3xl p-8 md:p-16 flex flex-col items-center text-center relative overflow-hidden group floating-hero-panel"
          >
            
            {/* Shifting background liquid glass blobs */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none rounded-3xl z-0">
              <div className="absolute top-[-20%] left-[-20%] w-[60%] h-[60%] bg-gradient-to-br from-cyan-400/20 to-blue-500/10 rounded-full filter blur-[80px] liquid-glass-blob-1" />
              <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] bg-gradient-to-tr from-purple-500/10 via-fuchsia-500/20 to-transparent rounded-full filter blur-[70px] liquid-glass-blob-2" />
              <div className="absolute top-[30%] left-[40%] w-[40%] h-[40%] bg-gradient-to-r from-emerald-400/15 to-cyan-500/10 rounded-full filter blur-[60px] liquid-glass-blob-3" />
            </div>

            {/* Ultra-realistic Physical Glass Glare Reflection Layer (Diagonal Sheen style from reference images) */}
            <div className="absolute inset-0 pointer-events-none rounded-3xl overflow-hidden z-20 select-none">
              {/* Dynamic sliding highlight sheen that reacts on group hover */}
              <div 
                className="absolute inset-0 transition-transform duration-1000 ease-out transform -translate-x-full group-hover:translate-x-full"
                style={{
                  background: 'linear-gradient(105deg, rgba(255,255,255,0) 39%, rgba(255,255,255,0.01) 41%, rgba(255,255,255,0.18) 45%, rgba(255,255,255,0.32) 48%, rgba(255,255,255,0.38) 49%, rgba(255,255,255,0.2) 51%, rgba(255,255,255,0.03) 55%, rgba(255,255,255,0) 60%)',
                  mixBlendMode: 'overlay',
                }}
              />
              {/* Double gloss reflection diagonal stripe matching first uploaded picture */}
              <div 
                className="absolute inset-0"
                style={{
                  background: 'linear-gradient(135deg, rgba(255,255,255,0) 40%, rgba(255,255,255,0.14) 43%, rgba(255,255,255,0.25) 45%, rgba(255,255,255,0.05) 48%, rgba(255,255,255,0.18) 50%, rgba(255,255,255,0.02) 52%, rgba(255,255,255,0) 58%)',
                  opacity: 0.8,
                  mixBlendMode: 'overlay',
                }}
              />
              {/* Luxury brass/gold bevel refraction ring mirroring the delicate golden edge of reference screen */}
              <div className="absolute inset-0 border border-yellow-400/15 rounded-3xl" />
              {/* Subtle top glare hotspot */}
              <div className="absolute top-0 inset-x-0 h-24 bg-gradient-to-b from-white/10 to-transparent blur-md" />
            </div>

            {/* Specular premium glass edge highlights */}
            <div className="absolute top-0 inset-x-0 h-[1.5px] bg-gradient-to-r from-transparent via-white/50 to-transparent opacity-80 z-10 pointer-events-none" />
            <div className="absolute inset-y-0 left-0 w-[1.5px] bg-gradient-to-b from-white/30 via-transparent to-white/10 opacity-60 z-10 pointer-events-none" />
            <div className="absolute top-2 left-4 text-[9px] font-mono text-cyan-400/30 tracking-[0.2em] relative z-20 pointer-events-none uppercase">NEXORAX GLASS SYSTEM</div>
            
            {/* Subtle, soft cinematic neon glow backgrounds */}
            <div className="absolute top-1/2 left-1/2 w-[110%] h-[340px] bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-cyan-500/10 via-transparent to-transparent blur-3xl pointer-events-none transform-gpu glow-orb-premium" />
            <div className="absolute -top-10 -right-10 w-48 h-48 bg-purple-500/5 rounded-full blur-3xl pointer-events-none"></div>
            <div className="absolute top-0 right-0 p-3 font-mono text-[8px] text-slate-500 select-none uppercase tracking-widest border-l border-b border-white/5">SYS_OS_V9.2</div>
            
            {/* Top Label */}
            <span className="text-[#00f0ff] font-mono text-[9px] sm:text-[11px] uppercase tracking-[0.45em] font-black block mb-5 drop-shadow-[0_0_8px_rgba(0,240,255,0.3)] relative z-10">
              NEXORAX DIGITAL INFRASTRUCTURE
            </span>
            <span className="hidden select-none">
            </span>

            {/* Supporting Highlight Tag - Launch with Confidence */}
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full liquid-glass-tagline shadow-2xl mb-6 z-10 transition-transform duration-300 hover:scale-105">
              <span className="w-1.5 h-1.5 rounded-full bg-[#00f0ff] animate-pulse shadow-[0_0_8px_#00f0ff]"></span>
              <span className="text-[10px] sm:text-xs font-mono uppercase tracking-[0.25em] text-white font-extrabold">
                Launch with Confidence.
              </span>
            </div>

            {/* Core Headline - Build Smarter. Scale Faster. */}
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-sans font-black tracking-tight mb-6 leading-[1.1] max-w-4xl relative z-10 text-white select-none">
              <span className="liquid-glass-heading-text block uppercase">
                Build Smarter. Scale Faster.
              </span>
            </h1>
            
            <div className="h-[1px] w-40 md:w-56 bg-gradient-to-r from-transparent via-cyan-500 to-transparent mb-8 opacity-40 relative z-10"></div>
            
            {/* Premium detailed copy */}
            <p className="text-slate-300 font-sans text-xs sm:text-sm md:text-base max-w-2xl leading-relaxed mb-10 relative z-10">
              We create premium digital systems, AI automation, and business infrastructure that help ambitious businesses launch faster, operate smarter, and grow with confidence.
            </p>
            
            {/* High-Fidelity Responsive CTA Button Array matching requested routes */}
            <div className="flex flex-wrap items-center justify-center gap-3 w-full max-w-4xl relative z-10 mb-10 px-4 sm:px-0">
              <button
                onClick={() => onNavigateLandingTab && onNavigateLandingTab('Digital Products')}
                className="px-5 py-3 sm:px-6 sm:py-3.5 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-mono text-[10.5px] font-black uppercase tracking-widest rounded-xl transition-all duration-300 shadow-[0_0_20px_rgba(236,72,153,0.3)] cursor-pointer active:scale-95 text-center flex items-center gap-1.5"
              >
                <span>Browse Digital Products</span>
                <ArrowUpRight className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => onNavigateLandingTab && onNavigateLandingTab('NexoraX Marketplace')}
                className="px-5 py-3 sm:px-6 sm:py-3.5 bg-[#00ffff]/10 hover:bg-[#00ffff]/20 border border-[#00ffff]/40 text-[#00ffff] font-mono text-[10.5px] font-black uppercase tracking-widest rounded-xl transition-all duration-300 cursor-pointer active:scale-95 text-center"
              >
                View Marketplace
              </button>
              <button
                onClick={() => onNavigateLandingTab && onNavigateLandingTab('Contact')}
                className="px-5 py-3 sm:px-6 sm:py-3.5 bg-zinc-900 border border-white/10 hover:border-white/30 text-white font-mono text-[10.5px] font-bold uppercase tracking-widest rounded-xl transition-all duration-300 cursor-pointer active:scale-95 text-center flex items-center gap-1.5 hover:bg-zinc-850"
              >
                <span>Book Consultation</span>
                <Sparkles className="w-3.5 h-3.5 text-cyan-400 animate-pulse" />
              </button>
              <button
                onClick={() => onNavigateLandingTab && onNavigateLandingTab('Guides & Videos')}
                className="px-5 py-3 sm:px-6 sm:py-3.5 bg-white/5 hover:bg-white/10 border border-white/5 text-slate-300 hover:text-white font-mono text-[10.5px] font-bold uppercase tracking-widest rounded-xl transition-all duration-300 cursor-pointer active:scale-95 text-center"
              >
                Guides & Videos
              </button>
              <button
                onClick={() => onNavigateLandingTab && onNavigateLandingTab('Tiers')}
                className="px-5 py-3 sm:px-6 sm:py-3.5 bg-[#061e38]/80 hover:bg-[#0a2c52]/80 border border-cyan-500/30 text-cyan-300 font-mono text-[10.5px] font-bold uppercase tracking-widest rounded-xl transition-all duration-300 cursor-pointer active:scale-95 text-center"
              >
                Pricing
              </button>
            </div>

            {/* Elegant Micro Line */}
            <div className="text-[9px] font-mono text-slate-500 uppercase tracking-[0.25em] relative z-10 border-t border-white/5 pt-4 w-full max-w-lg">
              Web • AI Automation • Search • Hosting • WorkSuite • Media • Growth Tools
            </div>
          </motion.div>
        </div>

        {/* SLIM AND ELEGANT SLIDING TICKER BANNER STRIP */}
        <div className="w-full overflow-hidden py-4 mt-16 border-y border-white/5 bg-black/40 backdrop-blur-md relative">
          <div className="ticker-animate-forward flex items-center gap-12 whitespace-nowrap">
            {roundedTickerItems.map((item, idx) => (
              <div key={idx} className="flex items-center gap-3 text-xs font-mono uppercase tracking-widest text-slate-400">
                <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 shadow-[0_0_8px_rgba(34,211,238,0.6)]"></span>
                <span className="hover:text-[#00f0ff] transition-colors duration-200">{item}</span>
              </div>
            ))}
          </div>
        </div>

{/* NEXORAX_BIG_MATRIX_RESTORE_START */}
<section className="relative z-10 pointer-events-auto my-12 overflow-hidden" style={{ width: '100vw', marginLeft: 'calc(50% - 50vw)', marginRight: 'calc(50% - 50vw)' }}>
  <style dangerouslySetInnerHTML={{ __html: `
    @keyframes nxGlassLeft {
      0% { transform: translateX(0); }
      100% { transform: translateX(-50%); }
    }

    @keyframes nxGlassRight {
      0% { transform: translateX(-50%); }
      100% { transform: translateX(0); }
    }

    .nx-glass-matrix {
      width: 100%;
      overflow: hidden;
      padding: 34px 0 30px;
      background:
        radial-gradient(circle at 16% 20%, rgba(168,85,247,.18), transparent 34%),
        radial-gradient(circle at 82% 10%, rgba(34,211,238,.14), transparent 30%),
        linear-gradient(90deg, rgba(2,6,23,.28), rgba(15,23,42,.34), rgba(2,6,23,.28));
      border-top: 1px solid rgba(255,255,255,.10);
      border-bottom: 1px solid rgba(255,255,255,.10);
      box-shadow:
        inset 0 1px 0 rgba(255,255,255,.08),
        inset 0 -1px 0 rgba(255,255,255,.05),
        0 0 70px rgba(34,211,238,.10);
      backdrop-filter: blur(14px) saturate(150%);
      -webkit-backdrop-filter: blur(14px) saturate(150%);
    }

    .nx-glass-title {
      text-align: center;
      margin-bottom: 26px;
      padding: 0 18px;
    }

    .nx-glass-title p {
      color: #e879f9;
      font-family: monospace;
      font-size: 10px;
      text-transform: uppercase;
      letter-spacing: .52em;
      margin-bottom: 8px;
    }

    .nx-glass-title h2 {
      color: rgba(240,253,255,.96);
      font-weight: 950;
      text-transform: uppercase;
      letter-spacing: -.035em;
      font-size: clamp(24px, 4vw, 52px);
      line-height: .95;
      text-shadow: 0 0 28px rgba(34,211,238,.35);
    }

    .nx-glass-row {
      width: 100%;
      overflow: hidden;
      -webkit-mask-image: linear-gradient(to right, transparent, black 3%, black 97%, transparent);
      mask-image: linear-gradient(to right, transparent, black 3%, black 97%, transparent);
    }

    .nx-glass-track {
      display: flex;
      width: max-content;
      gap: 2.8rem;
      white-space: nowrap;
      padding: 11px 0;
      will-change: transform;
    }

    .nx-glass-left {
      animation: nxGlassLeft 62s linear infinite;
    }

    .nx-glass-right {
      animation: nxGlassRight 70s linear infinite;
    }

    .nx-glass-matrix:hover .nx-glass-track {
      animation-play-state: paused;
    }

    .nx-glass-chip {
      display: inline-flex;
      align-items: center;
      gap: .68rem;
      color: rgba(248,250,252,.96);
      font-weight: 950;
      font-size: clamp(14px, 1.55vw, 23px);
      text-transform: uppercase;
      letter-spacing: -.015em;
      text-shadow:
        0 0 18px rgba(255,255,255,.22),
        0 0 28px rgba(34,211,238,.16);
    }

    .nx-glass-num {
      min-width: 2.15rem;
      height: 2.15rem;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      border-radius: 999px;
      color: #fff;
      font-family: monospace;
      font-size: .58em;
      font-weight: 950;
      background: rgba(168,85,247,.18);
      border: 1px solid rgba(34,211,238,.35);
      box-shadow: 0 0 18px rgba(168,85,247,.28), inset 0 1px 0 rgba(255,255,255,.18);
    }

    .nx-glass-icon {
      color: #22d3ee;
      filter: drop-shadow(0 0 8px rgba(34,211,238,.8));
      font-size: .95em;
    }

    .nx-glass-row:nth-child(odd) .nx-glass-icon {
      color: #a855f7;
      filter: drop-shadow(0 0 8px rgba(168,85,247,.85));
    }

    .nx-glass-footer {
      margin-top: 24px;
      text-align: center;
      color: #e879f9;
      font-family: monospace;
      font-size: 10px;
      text-transform: uppercase;
      letter-spacing: .52em;
    }

    @media (max-width: 700px) {
      .nx-glass-matrix { padding: 24px 0; }
      .nx-glass-track { gap: 1.8rem; }
      .nx-glass-title p,
      .nx-glass-footer {
        letter-spacing: .28em;
        font-size: 9px;
      }
      .nx-glass-num {
        min-width: 1.7rem;
        height: 1.7rem;
      }
    }
  ` }} />

  <div className="nx-glass-matrix">
    <div className="nx-glass-title">
      <p>NexoraX Universe Matrix</p>
      <h2>112 Digital Systems Moving In Sync</h2>
    </div>

    
    <div className="nx-glass-row">
      <div className={"nx-glass-track nx-glass-left"}>
        
          <span className="nx-glass-chip">
            <span className="nx-glass-num">1</span>
            <span className="nx-glass-icon">▰</span>
            <span>Revenue Engine</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">2</span>
            <span className="nx-glass-icon">👥</span>
            <span>Funnel Architect</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">3</span>
            <span className="nx-glass-icon">◆</span>
            <span>Ad Spend Optimizer</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">4</span>
            <span className="nx-glass-icon">⬡</span>
            <span>Influencer Outreach</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">5</span>
            <span className="nx-glass-icon">🎙</span>
            <span>Lead Generator</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">6</span>
            <span className="nx-glass-icon">🧠</span>
            <span>Sales Pipeline</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">7</span>
            <span className="nx-glass-icon">🛠</span>
            <span>Smart Quote</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">8</span>
            <span className="nx-glass-icon">▶</span>
            <span>Client Preview</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">9</span>
            <span className="nx-glass-icon">🔗</span>
            <span>Launch Pad</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">10</span>
            <span className="nx-glass-icon">👁</span>
            <span>QR Campaigns</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">11</span>
            <span className="nx-glass-icon">▭</span>
            <span>Voice Matrix</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">12</span>
            <span className="nx-glass-icon">💳</span>
            <span>Nexus Chat</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">13</span>
            <span className="nx-glass-icon">▰</span>
            <span>Nexus Search</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">14</span>
            <span className="nx-glass-icon">👥</span>
            <span>Nexus OS</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">15</span>
            <span className="nx-glass-icon">◆</span>
            <span>Marketplace</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">16</span>
            <span className="nx-glass-icon">⬡</span>
            <span>Product Builder</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">1</span>
            <span className="nx-glass-icon">🔗</span>
            <span>Revenue Engine</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">2</span>
            <span className="nx-glass-icon">👁</span>
            <span>Funnel Architect</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">3</span>
            <span className="nx-glass-icon">▭</span>
            <span>Ad Spend Optimizer</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">4</span>
            <span className="nx-glass-icon">💳</span>
            <span>Influencer Outreach</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">5</span>
            <span className="nx-glass-icon">▰</span>
            <span>Lead Generator</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">6</span>
            <span className="nx-glass-icon">👥</span>
            <span>Sales Pipeline</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">7</span>
            <span className="nx-glass-icon">◆</span>
            <span>Smart Quote</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">8</span>
            <span className="nx-glass-icon">⬡</span>
            <span>Client Preview</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">9</span>
            <span className="nx-glass-icon">🎙</span>
            <span>Launch Pad</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">10</span>
            <span className="nx-glass-icon">🧠</span>
            <span>QR Campaigns</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">11</span>
            <span className="nx-glass-icon">🛠</span>
            <span>Voice Matrix</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">12</span>
            <span className="nx-glass-icon">▶</span>
            <span>Nexus Chat</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">13</span>
            <span className="nx-glass-icon">🔗</span>
            <span>Nexus Search</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">14</span>
            <span className="nx-glass-icon">👁</span>
            <span>Nexus OS</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">15</span>
            <span className="nx-glass-icon">▭</span>
            <span>Marketplace</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">16</span>
            <span className="nx-glass-icon">💳</span>
            <span>Product Builder</span>
          </span>
      </div>
    </div>

    <div className="nx-glass-row">
      <div className={"nx-glass-track nx-glass-right"}>
        
          <span className="nx-glass-chip">
            <span className="nx-glass-num">17</span>
            <span className="nx-glass-icon">🔗</span>
            <span>Voice Synthesis</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">18</span>
            <span className="nx-glass-icon">👁</span>
            <span>Design Studio</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">19</span>
            <span className="nx-glass-icon">▭</span>
            <span>Print Studio</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">20</span>
            <span className="nx-glass-icon">💳</span>
            <span>Shorts Editor</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">21</span>
            <span className="nx-glass-icon">▰</span>
            <span>Neural Vault</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">22</span>
            <span className="nx-glass-icon">👥</span>
            <span>Media Studio</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">23</span>
            <span className="nx-glass-icon">◆</span>
            <span>UGCI Studio</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">24</span>
            <span className="nx-glass-icon">⬡</span>
            <span>Video Generator</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">25</span>
            <span className="nx-glass-icon">🎙</span>
            <span>Caption Engine</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">26</span>
            <span className="nx-glass-icon">🧠</span>
            <span>Poster Builder</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">27</span>
            <span className="nx-glass-icon">🛠</span>
            <span>Brand Studio</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">28</span>
            <span className="nx-glass-icon">▶</span>
            <span>Canva Workflow</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">29</span>
            <span className="nx-glass-icon">🔗</span>
            <span>TikTok Assets</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">30</span>
            <span className="nx-glass-icon">👁</span>
            <span>Campaign Assets</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">31</span>
            <span className="nx-glass-icon">▭</span>
            <span>Creative Desk</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">32</span>
            <span className="nx-glass-icon">💳</span>
            <span>Content Engine</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">17</span>
            <span className="nx-glass-icon">🎙</span>
            <span>Voice Synthesis</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">18</span>
            <span className="nx-glass-icon">🧠</span>
            <span>Design Studio</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">19</span>
            <span className="nx-glass-icon">🛠</span>
            <span>Print Studio</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">20</span>
            <span className="nx-glass-icon">▶</span>
            <span>Shorts Editor</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">21</span>
            <span className="nx-glass-icon">🔗</span>
            <span>Neural Vault</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">22</span>
            <span className="nx-glass-icon">👁</span>
            <span>Media Studio</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">23</span>
            <span className="nx-glass-icon">▭</span>
            <span>UGCI Studio</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">24</span>
            <span className="nx-glass-icon">💳</span>
            <span>Video Generator</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">25</span>
            <span className="nx-glass-icon">▰</span>
            <span>Caption Engine</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">26</span>
            <span className="nx-glass-icon">👥</span>
            <span>Poster Builder</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">27</span>
            <span className="nx-glass-icon">◆</span>
            <span>Brand Studio</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">28</span>
            <span className="nx-glass-icon">⬡</span>
            <span>Canva Workflow</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">29</span>
            <span className="nx-glass-icon">🎙</span>
            <span>TikTok Assets</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">30</span>
            <span className="nx-glass-icon">🧠</span>
            <span>Campaign Assets</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">31</span>
            <span className="nx-glass-icon">🛠</span>
            <span>Creative Desk</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">32</span>
            <span className="nx-glass-icon">▶</span>
            <span>Content Engine</span>
          </span>
      </div>
    </div>

    <div className="nx-glass-row">
      <div className={"nx-glass-track nx-glass-left"}>
        
          <span className="nx-glass-chip">
            <span className="nx-glass-num">33</span>
            <span className="nx-glass-icon">🎙</span>
            <span>Enterprise Web Hosting</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">34</span>
            <span className="nx-glass-icon">🧠</span>
            <span>Domain Names</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">35</span>
            <span className="nx-glass-icon">🛠</span>
            <span>Professional Emails</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">36</span>
            <span className="nx-glass-icon">▶</span>
            <span>API Key Generator</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">37</span>
            <span className="nx-glass-icon">🔗</span>
            <span>AI Code Generators</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">38</span>
            <span className="nx-glass-icon">👁</span>
            <span>Secure Infrastructure</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">39</span>
            <span className="nx-glass-icon">▭</span>
            <span>SSL Setup</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">40</span>
            <span className="nx-glass-icon">💳</span>
            <span>Business Email</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">41</span>
            <span className="nx-glass-icon">▰</span>
            <span>Hosting Monitor</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">42</span>
            <span className="nx-glass-icon">👥</span>
            <span>Cloud Systems</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">43</span>
            <span className="nx-glass-icon">◆</span>
            <span>WorkSuite</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">44</span>
            <span className="nx-glass-icon">⬡</span>
            <span>Client Dashboard</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">45</span>
            <span className="nx-glass-icon">🎙</span>
            <span>Payment Links</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">46</span>
            <span className="nx-glass-icon">🧠</span>
            <span>Invoice Engine</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">47</span>
            <span className="nx-glass-icon">🛠</span>
            <span>Secure Checkout</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">48</span>
            <span className="nx-glass-icon">▶</span>
            <span>Stripe Ready</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">33</span>
            <span className="nx-glass-icon">▰</span>
            <span>Enterprise Web Hosting</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">34</span>
            <span className="nx-glass-icon">👥</span>
            <span>Domain Names</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">35</span>
            <span className="nx-glass-icon">◆</span>
            <span>Professional Emails</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">36</span>
            <span className="nx-glass-icon">⬡</span>
            <span>API Key Generator</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">37</span>
            <span className="nx-glass-icon">🎙</span>
            <span>AI Code Generators</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">38</span>
            <span className="nx-glass-icon">🧠</span>
            <span>Secure Infrastructure</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">39</span>
            <span className="nx-glass-icon">🛠</span>
            <span>SSL Setup</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">40</span>
            <span className="nx-glass-icon">▶</span>
            <span>Business Email</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">41</span>
            <span className="nx-glass-icon">🔗</span>
            <span>Hosting Monitor</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">42</span>
            <span className="nx-glass-icon">👁</span>
            <span>Cloud Systems</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">43</span>
            <span className="nx-glass-icon">▭</span>
            <span>WorkSuite</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">44</span>
            <span className="nx-glass-icon">💳</span>
            <span>Client Dashboard</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">45</span>
            <span className="nx-glass-icon">▰</span>
            <span>Payment Links</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">46</span>
            <span className="nx-glass-icon">👥</span>
            <span>Invoice Engine</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">47</span>
            <span className="nx-glass-icon">◆</span>
            <span>Secure Checkout</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">48</span>
            <span className="nx-glass-icon">⬡</span>
            <span>Stripe Ready</span>
          </span>
      </div>
    </div>

    <div className="nx-glass-row">
      <div className={"nx-glass-track nx-glass-right"}>
        
          <span className="nx-glass-chip">
            <span className="nx-glass-num">49</span>
            <span className="nx-glass-icon">▰</span>
            <span>ROI Simulator</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">50</span>
            <span className="nx-glass-icon">👥</span>
            <span>Sentinel Pipeline</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">51</span>
            <span className="nx-glass-icon">◆</span>
            <span>Infiltration Map</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">52</span>
            <span className="nx-glass-icon">⬡</span>
            <span>Oracle Forecasts</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">53</span>
            <span className="nx-glass-icon">🎙</span>
            <span>Guardian Engine</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">54</span>
            <span className="nx-glass-icon">🧠</span>
            <span>Client Match</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">55</span>
            <span className="nx-glass-icon">🛠</span>
            <span>Lead Finder</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">56</span>
            <span className="nx-glass-icon">▶</span>
            <span>Deal Closer</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">57</span>
            <span className="nx-glass-icon">🔗</span>
            <span>Follow-Up Desk</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">58</span>
            <span className="nx-glass-icon">👁</span>
            <span>Money Desk</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">59</span>
            <span className="nx-glass-icon">▭</span>
            <span>Hot Leads</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">60</span>
            <span className="nx-glass-icon">💳</span>
            <span>Sales Agent</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">61</span>
            <span className="nx-glass-icon">▰</span>
            <span>Outreach Agent</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">62</span>
            <span className="nx-glass-icon">👥</span>
            <span>Review Monitor</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">63</span>
            <span className="nx-glass-icon">◆</span>
            <span>Trust Builder</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">64</span>
            <span className="nx-glass-icon">⬡</span>
            <span>Growth Compass</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">49</span>
            <span className="nx-glass-icon">🔗</span>
            <span>ROI Simulator</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">50</span>
            <span className="nx-glass-icon">👁</span>
            <span>Sentinel Pipeline</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">51</span>
            <span className="nx-glass-icon">▭</span>
            <span>Infiltration Map</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">52</span>
            <span className="nx-glass-icon">💳</span>
            <span>Oracle Forecasts</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">53</span>
            <span className="nx-glass-icon">▰</span>
            <span>Guardian Engine</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">54</span>
            <span className="nx-glass-icon">👥</span>
            <span>Client Match</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">55</span>
            <span className="nx-glass-icon">◆</span>
            <span>Lead Finder</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">56</span>
            <span className="nx-glass-icon">⬡</span>
            <span>Deal Closer</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">57</span>
            <span className="nx-glass-icon">🎙</span>
            <span>Follow-Up Desk</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">58</span>
            <span className="nx-glass-icon">🧠</span>
            <span>Money Desk</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">59</span>
            <span className="nx-glass-icon">🛠</span>
            <span>Hot Leads</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">60</span>
            <span className="nx-glass-icon">▶</span>
            <span>Sales Agent</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">61</span>
            <span className="nx-glass-icon">🔗</span>
            <span>Outreach Agent</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">62</span>
            <span className="nx-glass-icon">👁</span>
            <span>Review Monitor</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">63</span>
            <span className="nx-glass-icon">▭</span>
            <span>Trust Builder</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">64</span>
            <span className="nx-glass-icon">💳</span>
            <span>Growth Compass</span>
          </span>
      </div>
    </div>

    <div className="nx-glass-row">
      <div className={"nx-glass-track nx-glass-left"}>
        
          <span className="nx-glass-chip">
            <span className="nx-glass-num">65</span>
            <span className="nx-glass-icon">🔗</span>
            <span>Chronos Ledger</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">66</span>
            <span className="nx-glass-icon">👁</span>
            <span>The Pulse POS</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">67</span>
            <span className="nx-glass-icon">▭</span>
            <span>The Gavel</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">68</span>
            <span className="nx-glass-icon">💳</span>
            <span>Imperial Bridge</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">69</span>
            <span className="nx-glass-icon">▰</span>
            <span>Imperial Vault</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">70</span>
            <span className="nx-glass-icon">👥</span>
            <span>Quantum Billing</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">71</span>
            <span className="nx-glass-icon">◆</span>
            <span>Contract Desk</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">72</span>
            <span className="nx-glass-icon">⬡</span>
            <span>Compliance Logs</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">73</span>
            <span className="nx-glass-icon">🎙</span>
            <span>Security Logs</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">74</span>
            <span className="nx-glass-icon">🧠</span>
            <span>Remote Workplace</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">75</span>
            <span className="nx-glass-icon">🛠</span>
            <span>Team Tasks</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">76</span>
            <span className="nx-glass-icon">▶</span>
            <span>Work Submissions</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">77</span>
            <span className="nx-glass-icon">🔗</span>
            <span>Approval Centre</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">78</span>
            <span className="nx-glass-icon">👁</span>
            <span>Activity Feed</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">79</span>
            <span className="nx-glass-icon">▭</span>
            <span>Admin Control</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">80</span>
            <span className="nx-glass-icon">💳</span>
            <span>Director Gateway</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">65</span>
            <span className="nx-glass-icon">🎙</span>
            <span>Chronos Ledger</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">66</span>
            <span className="nx-glass-icon">🧠</span>
            <span>The Pulse POS</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">67</span>
            <span className="nx-glass-icon">🛠</span>
            <span>The Gavel</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">68</span>
            <span className="nx-glass-icon">▶</span>
            <span>Imperial Bridge</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">69</span>
            <span className="nx-glass-icon">🔗</span>
            <span>Imperial Vault</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">70</span>
            <span className="nx-glass-icon">👁</span>
            <span>Quantum Billing</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">71</span>
            <span className="nx-glass-icon">▭</span>
            <span>Contract Desk</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">72</span>
            <span className="nx-glass-icon">💳</span>
            <span>Compliance Logs</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">73</span>
            <span className="nx-glass-icon">▰</span>
            <span>Security Logs</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">74</span>
            <span className="nx-glass-icon">👥</span>
            <span>Remote Workplace</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">75</span>
            <span className="nx-glass-icon">◆</span>
            <span>Team Tasks</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">76</span>
            <span className="nx-glass-icon">⬡</span>
            <span>Work Submissions</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">77</span>
            <span className="nx-glass-icon">🎙</span>
            <span>Approval Centre</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">78</span>
            <span className="nx-glass-icon">🧠</span>
            <span>Activity Feed</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">79</span>
            <span className="nx-glass-icon">🛠</span>
            <span>Admin Control</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">80</span>
            <span className="nx-glass-icon">▶</span>
            <span>Director Gateway</span>
          </span>
      </div>
    </div>

    <div className="nx-glass-row">
      <div className={"nx-glass-track nx-glass-right"}>
        
          <span className="nx-glass-chip">
            <span className="nx-glass-num">81</span>
            <span className="nx-glass-icon">🎙</span>
            <span>Website LaunchCare</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">82</span>
            <span className="nx-glass-icon">🧠</span>
            <span>Mobile Apps</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">83</span>
            <span className="nx-glass-icon">🛠</span>
            <span>Booking Systems</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">84</span>
            <span className="nx-glass-icon">▶</span>
            <span>CRM Dashboard</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">85</span>
            <span className="nx-glass-icon">🔗</span>
            <span>AI Assistant</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">86</span>
            <span className="nx-glass-icon">👁</span>
            <span>QR Stickers</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">87</span>
            <span className="nx-glass-icon">▭</span>
            <span>Menu QR</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">88</span>
            <span className="nx-glass-icon">💳</span>
            <span>Tracking QR</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">89</span>
            <span className="nx-glass-icon">▰</span>
            <span>Lead Capture QR</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">90</span>
            <span className="nx-glass-icon">👥</span>
            <span>Social Automation</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">91</span>
            <span className="nx-glass-icon">◆</span>
            <span>SEO Growth</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">92</span>
            <span className="nx-glass-icon">⬡</span>
            <span>Google Visibility</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">93</span>
            <span className="nx-glass-icon">🎙</span>
            <span>Local Business Pack</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">94</span>
            <span className="nx-glass-icon">🧠</span>
            <span>Restaurant Pack</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">95</span>
            <span className="nx-glass-icon">🛠</span>
            <span>Salon Pack</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">96</span>
            <span className="nx-glass-icon">▶</span>
            <span>Trades Pack</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">81</span>
            <span className="nx-glass-icon">▰</span>
            <span>Website LaunchCare</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">82</span>
            <span className="nx-glass-icon">👥</span>
            <span>Mobile Apps</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">83</span>
            <span className="nx-glass-icon">◆</span>
            <span>Booking Systems</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">84</span>
            <span className="nx-glass-icon">⬡</span>
            <span>CRM Dashboard</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">85</span>
            <span className="nx-glass-icon">🎙</span>
            <span>AI Assistant</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">86</span>
            <span className="nx-glass-icon">🧠</span>
            <span>QR Stickers</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">87</span>
            <span className="nx-glass-icon">🛠</span>
            <span>Menu QR</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">88</span>
            <span className="nx-glass-icon">▶</span>
            <span>Tracking QR</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">89</span>
            <span className="nx-glass-icon">🔗</span>
            <span>Lead Capture QR</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">90</span>
            <span className="nx-glass-icon">👁</span>
            <span>Social Automation</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">91</span>
            <span className="nx-glass-icon">▭</span>
            <span>SEO Growth</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">92</span>
            <span className="nx-glass-icon">💳</span>
            <span>Google Visibility</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">93</span>
            <span className="nx-glass-icon">▰</span>
            <span>Local Business Pack</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">94</span>
            <span className="nx-glass-icon">👥</span>
            <span>Restaurant Pack</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">95</span>
            <span className="nx-glass-icon">◆</span>
            <span>Salon Pack</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">96</span>
            <span className="nx-glass-icon">⬡</span>
            <span>Trades Pack</span>
          </span>
      </div>
    </div>

    <div className="nx-glass-row">
      <div className={"nx-glass-track nx-glass-left"}>
        
          <span className="nx-glass-chip">
            <span className="nx-glass-num">97</span>
            <span className="nx-glass-icon">▰</span>
            <span>Startup Launch</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">98</span>
            <span className="nx-glass-icon">👥</span>
            <span>Retail Campaigns</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">99</span>
            <span className="nx-glass-icon">◆</span>
            <span>Service Business Pack</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">100</span>
            <span className="nx-glass-icon">⬡</span>
            <span>Callback Engine</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">101</span>
            <span className="nx-glass-icon">🎙</span>
            <span>WhatsApp Leads</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">102</span>
            <span className="nx-glass-icon">🧠</span>
            <span>Call Routing</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">103</span>
            <span className="nx-glass-icon">🛠</span>
            <span>Client Portal</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">104</span>
            <span className="nx-glass-icon">▶</span>
            <span>Preview Deposit</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">105</span>
            <span className="nx-glass-icon">🔗</span>
            <span>£199 Starter Offer</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">106</span>
            <span className="nx-glass-icon">👁</span>
            <span>Launch Systems</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">107</span>
            <span className="nx-glass-icon">▭</span>
            <span>Growth Engine</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">108</span>
            <span className="nx-glass-icon">💳</span>
            <span>Automation Hub</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">109</span>
            <span className="nx-glass-icon">▰</span>
            <span>Training Hub</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">110</span>
            <span className="nx-glass-icon">👥</span>
            <span>Guides & Videos</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">111</span>
            <span className="nx-glass-icon">◆</span>
            <span>NexoraX Studio</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">112</span>
            <span className="nx-glass-icon">⬡</span>
            <span>Digital Infrastructure</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">97</span>
            <span className="nx-glass-icon">🔗</span>
            <span>Startup Launch</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">98</span>
            <span className="nx-glass-icon">👁</span>
            <span>Retail Campaigns</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">99</span>
            <span className="nx-glass-icon">▭</span>
            <span>Service Business Pack</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">100</span>
            <span className="nx-glass-icon">💳</span>
            <span>Callback Engine</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">101</span>
            <span className="nx-glass-icon">▰</span>
            <span>WhatsApp Leads</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">102</span>
            <span className="nx-glass-icon">👥</span>
            <span>Call Routing</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">103</span>
            <span className="nx-glass-icon">◆</span>
            <span>Client Portal</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">104</span>
            <span className="nx-glass-icon">⬡</span>
            <span>Preview Deposit</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">105</span>
            <span className="nx-glass-icon">🎙</span>
            <span>£199 Starter Offer</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">106</span>
            <span className="nx-glass-icon">🧠</span>
            <span>Launch Systems</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">107</span>
            <span className="nx-glass-icon">🛠</span>
            <span>Growth Engine</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">108</span>
            <span className="nx-glass-icon">▶</span>
            <span>Automation Hub</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">109</span>
            <span className="nx-glass-icon">🔗</span>
            <span>Training Hub</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">110</span>
            <span className="nx-glass-icon">👁</span>
            <span>Guides & Videos</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">111</span>
            <span className="nx-glass-icon">▭</span>
            <span>NexoraX Studio</span>
          </span>
          <span className="nx-glass-chip">
            <span className="nx-glass-num">112</span>
            <span className="nx-glass-icon">💳</span>
            <span>Digital Infrastructure</span>
          </span>
      </div>
    </div>

    <div className="nx-glass-footer">Design Studio Offerings</div>
  </div>
</section>
{/* NEXORAX_BIG_MATRIX_RESTORE_END */}

{/* Safe Campaign Public Offers Section */}
        <div className="w-full max-w-7xl mx-auto px-6 z-10 mt-16 pb-16 relative">
          <div className="text-center space-y-3 mb-12">
            <span className="text-[#00f0ff] font-mono text-[10px] uppercase tracking-[0.3em] font-extrabold block">
              OUR SOLUTIONS & SERVICES
            </span>
            <h2 className="text-3xl md:text-4.5xl font-sans font-black text-white uppercase tracking-tight">
              Premium Digital & AI Solutions
            </h2>
            <p className="text-zinc-400 font-sans text-xs md:text-sm max-w-2xl mx-auto leading-relaxed">
              NexoraX helps businesses and creators launch websites, QR campaigns, social media packs, UGC scripts, captions, AI chat support and automation systems.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pointer-events-auto">
            {/* Offer A */}
            <div className="liquid-glass-panel p-6 rounded-2xl border border-white/10 hover:border-cyan-500/30 transition-all duration-300 hover:scale-[1.01] flex flex-col justify-between">
              <div className="space-y-3">
                <div className="w-10 h-10 rounded-xl bg-cyan-500/10 border border-cyan-500/25 flex items-center justify-center text-cyan-400">
                  <Globe className="w-5 h-5" />
                </div>
                <h3 className="text-lg font-black text-white uppercase tracking-wide">Website LaunchCare</h3>
                <p className="text-zinc-400 text-xs leading-relaxed">
                  Starter website setup, premium layouts, fast deployment, and monthly hosting/maintenance support. Direct payment or subscription plans available.
                </p>
              </div>
              <div className="mt-6 flex items-center justify-between pt-4 border-t border-white/5">
                <span className="text-cyan-300 font-mono text-xs font-bold">From £25 / mo</span>
                <button 
                  onClick={() => onNavigateLandingTab && onNavigateLandingTab('Tiers')}
                  className="px-3.5 py-1.5 bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/40 text-cyan-300 font-mono text-[9px] font-black uppercase tracking-wider rounded-lg transition-colors cursor-pointer"
                >
                  View Plans
                </button>
              </div>
            </div>

            {/* Offer B */}
            <div className="liquid-glass-panel p-6 rounded-2xl border border-white/10 hover:border-purple-500/30 transition-all duration-300 hover:scale-[1.01] flex flex-col justify-between">
              <div className="space-y-3">
                <div className="w-10 h-10 rounded-xl bg-purple-500/10 border border-purple-500/25 flex items-center justify-center text-purple-400">
                  <Video className="w-5 h-5" />
                </div>
                <h3 className="text-lg font-black text-white uppercase tracking-wide">AI Creator & UGC Support</h3>
                <p className="text-zinc-400 text-xs leading-relaxed">
                  Managed content services generating high-converting hooks, custom video script templates, captions, campaign ad copy, and strategic short-form plans.
                </p>
              </div>
              <div className="mt-6 flex items-center justify-between pt-4 border-t border-white/5">
                <span className="text-purple-300 font-mono text-xs font-bold">Managed Service</span>
                <button 
                  onClick={() => onNavigateLandingTab && onNavigateLandingTab('Contact')}
                  className="px-3.5 py-1.5 bg-purple-500/10 hover:bg-purple-500/20 border border-purple-500/40 text-purple-300 font-mono text-[9px] font-black uppercase tracking-wider rounded-lg transition-colors cursor-pointer"
                >
                  Request Info
                </button>
              </div>
            </div>

            {/* Offer C */}
            <div className="liquid-glass-panel p-6 rounded-2xl border border-white/10 hover:border-pink-500/30 transition-all duration-300 hover:scale-[1.01] flex flex-col justify-between">
              <div className="space-y-3">
                <div className="w-10 h-10 rounded-xl bg-pink-500/10 border border-pink-500/25 flex items-center justify-center text-pink-400">
                  <Layers className="w-5 h-5" />
                </div>
                <h3 className="text-lg font-black text-white uppercase tracking-wide">Social Campaign Packs</h3>
                <p className="text-zinc-400 text-xs leading-relaxed">
                  Tailored campaign asset packs containing posts, caption copy, ad creative concepts, local hooks, and ready-to-import content scheduling calendars.
                </p>
              </div>
              <div className="mt-6 flex items-center justify-between pt-4 border-t border-white/5">
                <span className="text-pink-300 font-mono text-xs font-bold">From £25 / mo</span>
                <button 
                  onClick={() => onNavigateLandingTab && onNavigateLandingTab('Tiers')}
                  className="px-3.5 py-1.5 bg-pink-500/10 hover:bg-pink-500/20 border border-pink-500/40 text-pink-300 font-mono text-[9px] font-black uppercase tracking-wider rounded-lg transition-colors cursor-pointer"
                >
                  View Packs
                </button>
              </div>
            </div>

            {/* Offer D */}
            <div className="liquid-glass-panel p-6 rounded-2xl border border-white/10 hover:border-emerald-500/30 transition-all duration-300 hover:scale-[1.01] flex flex-col justify-between">
              <div className="space-y-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/25 flex items-center justify-center text-emerald-400">
                  <Printer className="w-5 h-5" />
                </div>
                <h3 className="text-lg font-black text-white uppercase tracking-wide">QR Campaign Packs</h3>
                <p className="text-zinc-400 text-xs leading-relaxed">
                  Physical and digital QR integration: QR restaurant menus, QR business cards, contactless review standees, and custom lead generation landing pages.
                </p>
              </div>
              <div className="mt-6 flex items-center justify-between pt-4 border-t border-white/5">
                <span className="text-emerald-300 font-mono text-xs font-bold">Managed Setup</span>
                <button 
                  onClick={() => onNavigateLandingTab && onNavigateLandingTab('Contact')}
                  className="px-3.5 py-1.5 bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 font-mono text-[9px] font-black uppercase tracking-wider rounded-lg transition-colors cursor-pointer"
                >
                  Contact Us
                </button>
              </div>
            </div>

            {/* Offer E */}
            <div className="liquid-glass-panel p-6 rounded-2xl border border-white/10 hover:border-amber-500/30 transition-all duration-300 hover:scale-[1.01] flex flex-col justify-between">
              <div className="space-y-3">
                <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/25 flex items-center justify-center text-amber-400">
                  <Map className="w-5 h-5" />
                </div>
                <h3 className="text-lg font-black text-white uppercase tracking-wide">Driver & Fleet Systems</h3>
                <p className="text-zinc-400 text-xs leading-relaxed">
                  Bespoke logistics dispatch networks, driver route planners, real-time tracking engines, and proof of delivery reporting systems. Custom integrations.
                </p>
              </div>
              <div className="mt-6 flex items-center justify-between pt-4 border-t border-white/5">
                <span className="text-amber-300 font-mono text-xs font-bold">Quote Only</span>
                <button 
                  onClick={() => {
                    onNavigateLandingTab && onNavigateLandingTab('Tiers');
                    setTimeout(() => {
                      window.dispatchEvent(new CustomEvent('nexorax-trigger-driver-quote'));
                    }, 150);
                  }}
                  className="px-3.5 py-1.5 bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/40 text-amber-300 font-mono text-[9px] font-black uppercase tracking-wider rounded-lg transition-colors cursor-pointer"
                >
                  Get Quote
                </button>
              </div>
            </div>

            {/* Offer F */}
            <div className="liquid-glass-panel p-6 rounded-2xl border border-white/10 hover:border-indigo-500/30 transition-all duration-300 hover:scale-[1.01] flex flex-col justify-between">
              <div className="space-y-3">
                <div className="w-10 h-10 rounded-xl bg-indigo-500/10 border border-indigo-500/25 flex items-center justify-center text-indigo-400">
                  <Cpu className="w-5 h-5" />
                </div>
                <h3 className="text-lg font-black text-white uppercase tracking-wide">Mobile Apps & Portals</h3>
                <p className="text-zinc-400 text-xs leading-relaxed">
                  Premium B2B mobile app prototypes, booking engines, client workspaces, custom portals, and tailored software system layouts. Request quote.
                </p>
              </div>
              <div className="mt-6 flex items-center justify-between pt-4 border-t border-white/5">
                <span className="text-indigo-300 font-mono text-xs font-bold">Quote Only</span>
                <button 
                  onClick={() => onNavigateLandingTab && onNavigateLandingTab('Contact')}
                  className="px-3.5 py-1.5 bg-indigo-500/10 hover:bg-indigo-500/20 border border-indigo-500/40 text-indigo-300 font-mono text-[9px] font-black uppercase tracking-wider rounded-lg transition-colors cursor-pointer"
                >
                  Get Quote
                </button>
              </div>
            </div>

            {/* Offer G */}
            <div className="liquid-glass-panel p-6 rounded-2xl border border-white/10 hover:border-blue-500/30 transition-all duration-300 hover:scale-[1.01] flex flex-col justify-between lg:col-span-3 lg:max-w-md lg:mx-auto lg:w-full">
              <div className="space-y-3">
                <div className="w-10 h-10 rounded-xl bg-blue-500/10 border border-blue-500/25 flex items-center justify-center text-blue-400">
                  <Mail className="w-5 h-5" />
                </div>
                <h3 className="text-lg font-black text-white uppercase tracking-wide">Business Email & Hosting</h3>
                <p className="text-zinc-400 text-xs leading-relaxed">
                  Managed domain registration, secure SMTP/IMAP business email inbox setup (Google Workspace or private server shunts), SSL setup, and continuous maintenance.
                </p>
              </div>
              <div className="mt-6 flex items-center justify-between pt-4 border-t border-white/5">
                <span className="text-blue-300 font-mono text-xs font-bold">Managed Service</span>
                <button 
                  onClick={() => onNavigateLandingTab && onNavigateLandingTab('Contact')}
                  className="px-3.5 py-1.5 bg-blue-500/10 hover:bg-blue-500/20 border border-blue-500/40 text-blue-300 font-mono text-[9px] font-black uppercase tracking-wider rounded-lg transition-colors cursor-pointer"
                >
                  Get Started
                </button>
              </div>
            </div>
          </div>
        </div>
    </div>
  );
};
